import { useMemo } from 'react';
import { CoreBox, CoreChip, useTheme } from '@vitvio/design-system';
import { getMinutesSinceMidnightInTimezone } from '@vitvio/helpers';
import { useTimeProviderContext } from '@vitvio/time-components';
import { useUserContext } from '@vitvio/user-context';
import { MIN_REMAINING_DURATION_TO_SHOW } from '../../constants';
import { useMissionControlCalendarContext } from '../context/MissionControlCalendarContext';
import type { CapacitySlot } from '../types';

export function CapacityCard(props: CapacitySlot) {
  const { getLayout } = useTheme();
  const { hospitalTimezone } = useUserContext();
  const { currentTimeInTimezone } = useTimeProviderContext();
  const { selectedDate } = useMissionControlCalendarContext();
  const minuteHeight = getLayout('timeline', 'minuteHeight', false);
  const cardMargin = getLayout('timeline', 'cardMargin', false);
  const linePosition = getMinutesSinceMidnightInTimezone(currentTimeInTimezone, selectedDate, hospitalTimezone);

  const { isVisible, innerHeight, displayDuration } = useMemo(() => {
    const { startPosition, endPosition } = props;
    const totalDuration = endPosition - startPosition;
    const remaining = linePosition < startPosition ? totalDuration : Math.max(0, endPosition - linePosition);
    const totalSpaceBetweenCards = totalDuration * minuteHeight - cardMargin;
    const capacityTagDivHeight = remaining * minuteHeight - cardMargin;
    const displayDuration = Math.round(remaining);
    const innerHeight = Math.max(0, Math.min(totalSpaceBetweenCards, capacityTagDivHeight));
    const isVisible = remaining > MIN_REMAINING_DURATION_TO_SHOW;
    return {
      isVisible,
      innerHeight: innerHeight,
      displayDuration: displayDuration,
    };
  }, [props, linePosition, minuteHeight, cardMargin]);

  if (linePosition >= props.endPosition || !isVisible) return null;

  return (
    <CoreBox mx="3" fullH flexRow>
      <CoreBox mt="auto" h={innerHeight} fullW flexRow>
        <CoreBox my="auto" mx="auto">
          <CoreChip
            path="missionControl.timeline.capacity"
            values={{ duration: displayDuration }}
            iconLeft="timelapse"
            variant="flat"
            size="sm"
            color="primary"
          />
        </CoreBox>
      </CoreBox>
    </CoreBox>
  );
}
